import type { JVSIcons } from '@mairistem/composants';
import { deburr } from 'lodash';
import PerimetreIdee, {
	PERIMETRES_IDEE,
} from '../../constants/simulation/perimetreIdee';
import type {
	default as IdeeImportable,
	ImputationIdee,
	ValeursPerimetresIdee,
} from '../../interfaces/simulation/ideeImportable';

type SegmentImputation = keyof ImputationIdee;

// Intitule (au survol), picto de la fiche idee Vision 360 et libelle affiche
// quand le segment est absent
export const SEGMENTS_IMPUTATION: Record<
	SegmentImputation,
	{ absent: string; icone: JVSIcons; intitule: string }
> = {
	section: {
		absent: 'Aucune section',
		icone: 'Layers',
		intitule: 'Section',
	},
	famille: {
		absent: 'Aucune famille',
		icone: 'SquareLibrary',
		intitule: 'Famille',
	},
	sousFamille: {
		absent: 'Aucune sous-famille',
		icone: 'LibraryBig',
		intitule: 'Sous-famille',
	},
	article: {
		absent: 'Aucun article',
		icone: 'BookText',
		intitule: 'Article comptable',
	},
};

// Rang de chaque cran dans l'ordre d'elargissement
const RANGS = Object.fromEntries(
	PERIMETRES_IDEE.map((perimetre, rang) => [perimetre, rang]),
) as Record<PerimetreIdee, number>;

// Segments rappeles sur une idee : seuls ceux que le cran ne garantit pas
// deja, soit les crans plus stricts (les crans hors "toutes" portent le nom de
// leur segment), du plus large au plus strict
export const getSegmentsImputation = (perimetre: PerimetreIdee) =>
	PERIMETRES_IDEE.slice(0, RANGS[perimetre]).reverse() as SegmentImputation[];

const TITRES: Record<PerimetreIdee, string> = {
	[PerimetreIdee.ARTICLE]: 'Même article',
	[PerimetreIdee.SOUS_FAMILLE]: 'Même sous-famille',
	[PerimetreIdee.FAMILLE]: 'Même famille',
	[PerimetreIdee.SECTION]: 'Même section',
	[PerimetreIdee.TOUTES]: 'Toutes les idées',
};

const ELARGISSEMENTS: Partial<Record<PerimetreIdee, string>> = {
	[PerimetreIdee.SOUS_FAMILLE]: 'à la sous-famille',
	[PerimetreIdee.FAMILLE]: 'à la famille',
	[PerimetreIdee.SECTION]: 'à la section',
	[PerimetreIdee.TOUTES]: 'à toutes les idées',
};

const AIDES: Record<PerimetreIdee, (valeur: string | null) => string> = {
	[PerimetreIdee.ARTICLE]: (valeur) =>
		`Seules les idées imputées sur l'article ${valeur}, celui de la ligne budgétaire, sont proposées.`,
	[PerimetreIdee.SOUS_FAMILLE]: (valeur) =>
		`Les idées de la sous-famille ${valeur} sont proposées, y compris celles imputées sur un autre article de cette sous-famille ou sans article.`,
	[PerimetreIdee.FAMILLE]: (valeur) =>
		`Les idées de la famille ${valeur} sont proposées, toutes sous-familles confondues.`,
	[PerimetreIdee.SECTION]: (valeur) =>
		`Les idées de la section ${valeur} sont proposées, toutes familles confondues.`,
	[PerimetreIdee.TOUTES]: () =>
		"Toutes les idées sont proposées, quelle que soit leur imputation. Vérifiez qu'elles relèvent bien de cette ligne budgétaire.",
};

export const getTitrePerimetre = (perimetre: PerimetreIdee) =>
	TITRES[perimetre];

const getValeurLigne = (
	perimetre: PerimetreIdee,
	valeurs: ValeursPerimetresIdee,
): string | null =>
	perimetre === PerimetreIdee.TOUTES
		? null
		: valeurs[perimetre as keyof ValeursPerimetresIdee];

export const getValeurPerimetre = (
	perimetre: PerimetreIdee,
	valeurs: ValeursPerimetresIdee,
) =>
	perimetre === PerimetreIdee.TOUTES
		? "Sans restriction d'imputation"
		: (getValeurLigne(perimetre, valeurs) ??
			SEGMENTS_IMPUTATION[perimetre as SegmentImputation].absent);

// Libelle du champ ferme : le dernier cran n'a pas de valeur a rappeler
export const getLibelleChampPerimetre = (
	perimetre: PerimetreIdee,
	valeurs: ValeursPerimetresIdee,
) =>
	perimetre === PerimetreIdee.TOUTES
		? getTitrePerimetre(perimetre)
		: `${getTitrePerimetre(perimetre)} : ${getValeurPerimetre(perimetre, valeurs)}`;

export const getPerimetreSuivant = (perimetre: PerimetreIdee) =>
	PERIMETRES_IDEE[RANGS[perimetre] + 1];

// Chaque cran inclut les idees des crans plus stricts
export const getIdeesDuPerimetre = (
	idees: IdeeImportable[],
	perimetre: PerimetreIdee,
) => {
	const rang = RANGS[perimetre];
	return idees.filter((idee) => RANGS[idee.perimetre] <= rang);
};

// Nombre d'idees proposees sur chaque cran (cumulatif), en une seule passe
export const compterIdeesParPerimetre = (idees: IdeeImportable[]) => {
	const nombresParRang = PERIMETRES_IDEE.map(() => 0);
	for (const idee of idees) nombresParRang[RANGS[idee.perimetre]] += 1;
	let cumul = 0;
	return Object.fromEntries(
		PERIMETRES_IDEE.map((perimetre, rang) => {
			cumul += nombresParRang[rang];
			return [perimetre, cumul];
		}),
	) as Record<PerimetreIdee, number>;
};

export const formatNombreIdees = (nombre: number) =>
	`${nombre} idée${nombre > 1 ? 's' : ''}`;

const normaliser = (valeur: string) => deburr(valeur.toLocaleLowerCase());

// Recherche sur le seul libelle de l'idee, sans tenir compte de la casse ni
// des accents
export const filtrerIdeesParLibelle = (
	idees: IdeeImportable[],
	recherche: string,
) => {
	const texte = normaliser(recherche.trim());
	if (!texte) return idees;
	return idees.filter((idee) => normaliser(idee.libelle).includes(texte));
};

export const getLibelleSelection = (nombre: number) =>
	nombre === 0
		? 'Aucune idée sélectionnée'
		: `${formatNombreIdees(nombre)} sélectionnée${nombre > 1 ? 's' : ''}`;

// Le nombre rappele est celui des idees ajoutees par le cran suivant ; au
// dernier cran, le bouton ramene au premier
export const getLibelleBoutonPerimetre = (
	perimetre: PerimetreIdee,
	nombres: Record<PerimetreIdee, number>,
) => {
	const suivant = getPerimetreSuivant(perimetre);
	if (!suivant) return "Restreindre à l'article";
	return `Élargir ${ELARGISSEMENTS[suivant]} (+${nombres[suivant] - nombres[perimetre]})`;
};

export const getAidePerimetre = (
	perimetre: PerimetreIdee,
	valeurs: ValeursPerimetresIdee,
) => {
	const valeur = getValeurLigne(perimetre, valeurs);
	// Sans rattachement de la ligne, un cran intermediaire ne propose que les
	// idees de son article
	if (
		!valeur &&
		perimetre !== PerimetreIdee.ARTICLE &&
		perimetre !== PerimetreIdee.TOUTES
	) {
		return `La ligne budgétaire n'est rattachée à ${SEGMENTS_IMPUTATION[perimetre as SegmentImputation].absent.toLocaleLowerCase()} : seules les idées de son article sont proposées.`;
	}
	return AIDES[perimetre](valeur);
};
